import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";

const MAX_OP = 6;

export default function StampaOrdineA4() {
    const { id } = useParams();
    const [o, setO] = useState(null);
    const [operatori, setOperatori] = useState([]);
    // slots: array di {op, turno, data}. Default 1 slot pre-compilato con i dati dell'ordine.
    const [slots, setSlots] = useState([{op: "", turno: "I", data: ""}]);
    // pages: null = pannello di preparazione, altrimenti array di {id, name, turno, data} da stampare
    const [pages, setPages] = useState(null);
    const company = "PRODUX · Linea Saldatura";

    useEffect(() => {
        (async () => {
            try {
                const [ord, op] = await Promise.all([
                    api.get("/ordini"),
                    api.get("/users/operatori").catch(() => ({ data: [] })),
                ]);
                const found = ord.data.find(x => x.id === id) || null;
                setO(found);
                setOperatori(op.data || []);
                // Pre-compila lo slot 0 con operatore + turno + data già scelti dall'ufficio.
                // L'operatore viene inserito solo se figura nella lista operatori saldatura.
                if (found) {
                    const opList = op.data || [];
                    const preOp = found.operatore_id && opList.some(x => x.id === found.operatore_id)
                        ? found.operatore_id : "";
                    setSlots(prev => prev.map((s, i) => i === 0 ? {
                        ...s,
                        op: preOp,
                        turno: found.turno || "I",
                        data: (found.data_lavorazione || "").slice(0, 10),
                    } : s));
                }
            } catch (e) { console.error("load ordine failed", e); setO(null); }
        })();
    }, [id]);

    // Trigger stampa quando pages è pronto
    useEffect(() => {
        if (pages && pages.length > 0) {
            const t = setTimeout(() => { try { window.print(); } catch (e) { console.error("print failed", e); } }, 400);
            return () => clearTimeout(t);
        }
    }, [pages]);

    // Se l'ordine NON è multi_operatore, salta il pannello di preparazione e stampa direttamente
    // una singola copia con turno + data dell'ordine (comportamento pre-multi-op).
    useEffect(() => {
        if (o && !o.multi_operatore && pages === null) {
            setPages([{ id: "", name: "", turno: o.turno || "I", data: (o.data_lavorazione || "").slice(0, 10) }]);
        }
    }, [o, pages]);

    const selectedIds = useMemo(() => slots.map(s => s.op).filter(Boolean), [slots]);

    const setSlotOp = (i, val) => {
        setSlots(prev => prev.map((s, idx) => idx === i ? {...s, op: val} : s));
    };
    const setSlotTurno = (i, val) => {
        setSlots(prev => prev.map((s, idx) => idx === i ? {...s, turno: val} : s));
    };
    const setSlotData = (i, val) => {
        setSlots(prev => prev.map((s, idx) => idx === i ? {...s, data: val} : s));
    };
    // Aggiunge giorni a una data ISO YYYY-MM-DD
    const addDays = (isoDate, days) => {
        if (!isoDate) return "";
        try {
            const d = new Date(`${isoDate}T00:00:00`);
            d.setDate(d.getDate() + days);
            return d.toISOString().slice(0, 10);
        } catch { return isoDate; }
    };
    const addSlot = () => {
        if (slots.length >= MAX_OP) return;
        setSlots(prev => {
            const last = prev[prev.length - 1] || {turno: "I", data: ""};
            // Rotazione turno I → II → III → I; quando torna a I, avanza al giorno successivo
            const rollover = last.turno === "III";
            const nextTurno = rollover ? "I" : (last.turno === "I" ? "II" : "III");
            const nextData = rollover ? addDays(last.data, 1) : last.data;
            return [...prev, {op: "", turno: nextTurno, data: nextData}];
        });
    };
    const removeSlot = (i) => {
        setSlots(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);
    };

    const confirmStampa = () => {
        const chosen = slots
            .filter(s => s.op)
            .map(s => {
                const opObj = operatori.find(x => x.id === s.op);
                return opObj ? {
                    id: opObj.id,
                    name: opObj.name,
                    turno: s.turno || o?.turno || "I",
                    data: s.data || (o?.data_lavorazione || "").slice(0, 10),
                } : null;
            })
            .filter(Boolean);
        if (chosen.length === 0) {
            setPages([{
                id: "", name: "",
                turno: slots[0]?.turno || o?.turno || "I",
                data: slots[0]?.data || (o?.data_lavorazione || "").slice(0, 10),
            }]);
        } else {
            setPages(chosen);
        }
    };
    const confirmStampaSenzaOp = () => setPages([{
        id: "", name: "",
        turno: o?.turno || "I",
        data: (o?.data_lavorazione || "").slice(0, 10),
    }]);

    if (!o) return <div style={{padding:40, fontFamily:"system-ui", background:"white", color:"black"}}>Caricamento ordine…</div>;

    const url = `${window.location.origin}/produzione?id=${id}`;
    const imballoEff = o.imballo_eff || o.imballo || "cartoni";
    const isVas = imballoEff === "vaschette";
    const unitUpper = isVas ? "VASCHETTE" : "CARTONI";
    const unitTitle = isVas ? "Vaschette" : "Cartoni";
    const wPrinc = o.larghezza_principale_eff || 0;
    const wSoff = o.larghezza_soffietto_eff || 0;
    const wTot = wPrinc + wSoff;
    const lLen = o.lunghezza_eff || 0;
    const sMy = o.spessore_my || 0;
    const pBusta = (lLen && wTot && sMy) ? (lLen * wTot * sMy * 0.000092 * 2) : 0;
    const pRotolo = pBusta * (o.buste_per_rotolo_eff || 0);
    const pCartone = pRotolo * (o.rotoli_per_cartone_eff || 0) / 1000;
    const confezRxB = `${o.rotoli_per_cartone_eff || "-"} rotoli × ${o.buste_per_rotolo_eff || "-"} buste`;
    // Data in italiano esteso: "28 settembre 2026"
    const fmtDataLunga = (s) => {
        if (!s) return "-";
        try {
            const iso = s.length === 10 ? `${s}T00:00:00` : s;
            const d = new Date(iso);
            if (isNaN(d.getTime())) return s.slice(0, 10);
            return d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
        } catch { return s.slice(0, 10); }
    };
    const data_ = fmtDataLunga(o.data_lavorazione);

    // Pannello preparazione (pages non ancora impostato)
    if (!pages) {
        const canConfirm = true; // sempre attivo: se nessun operatore usa fallback
        return (
            <div style={{
                minHeight: "100vh",
                background: "#0a0a0a",
                color: "white",
                padding: "32px 20px",
                fontFamily: "system-ui, -apple-system, sans-serif",
            }} data-testid="prep-stampa-saldatura">
                <div style={{ maxWidth: 720, margin: "0 auto" }}>
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 12, letterSpacing: 2, opacity: 0.6, fontFamily: "monospace" }}>PRODUX · STAMPA ORDINE</div>
                        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "6px 0 4px" }}>Prepara stampa</h1>
                        <div style={{ opacity: 0.75, fontSize: 14 }}>
                            Ordine <b style={{ fontFamily: "monospace" }}>{o.numero}</b> · {o.codice} · {o.descrizione}
                        </div>
                    </div>

                    <div style={{
                        background: "#141414",
                        border: "1px solid #262626",
                        borderRadius: 12,
                        padding: 20,
                    }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 15 }}>Operatori saldatura</div>
                                <div style={{ opacity: 0.6, fontSize: 12, marginTop: 2 }}>
                                    Verrà stampata una copia identica del foglio per ciascun operatore selezionato.
                                </div>
                            </div>
                            <div style={{ fontFamily: "monospace", fontSize: 12, opacity: 0.6 }}>
                                {selectedIds.length}/{MAX_OP}
                            </div>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {slots.map((slot, i) => {
                                const sid = slot.op;
                                const sturno = slot.turno || "I";
                                const sdata = slot.data || "";
                                const takenElsewhere = new Set(slots.filter((v, idx) => idx !== i && v.op).map(v => v.op));
                                return (
                                    <div key={i} style={{
                                        display: "flex", flexDirection: "column", gap: 8,
                                        padding: 10, background: "#0f0f0f", border: "1px solid #1f1f1f", borderRadius: 10,
                                    }}>
                                        {/* Riga 1: numero slot + operatore + rimuovi */}
                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <div style={{
                                                width: 32, height: 32, borderRadius: 8,
                                                background: sid ? "#f59e0b" : "#262626",
                                                color: sid ? "black" : "#a3a3a3",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                fontWeight: 900, fontFamily: "monospace", fontSize: 13,
                                                flexShrink: 0,
                                            }}>{i + 1}</div>
                                            <select
                                                data-testid={`slot-operatore-${i}`}
                                                value={sid}
                                                onChange={e => setSlotOp(i, e.target.value)}
                                                style={{
                                                    flex: 1,
                                                    background: "#0a0a0a",
                                                    color: "white",
                                                    border: "1px solid #333",
                                                    borderRadius: 8,
                                                    padding: "10px 12px",
                                                    fontSize: 14,
                                                    outline: "none",
                                                }}
                                            >
                                                <option value="">— Seleziona operatore —</option>
                                                {operatori.map(op => (
                                                    <option
                                                        key={op.id}
                                                        value={op.id}
                                                        disabled={takenElsewhere.has(op.id)}
                                                    >{op.name}{takenElsewhere.has(op.id) ? " (già scelto)" : ""}</option>
                                                ))}
                                            </select>
                                            <button
                                                data-testid={`slot-remove-${i}`}
                                                onClick={() => removeSlot(i)}
                                                disabled={slots.length <= 1}
                                                title="Rimuovi"
                                                style={{
                                                    width: 36, height: 36, borderRadius: 8,
                                                    background: slots.length <= 1 ? "#1a1a1a" : "#262626",
                                                    color: slots.length <= 1 ? "#555" : "white",
                                                    border: "1px solid #333",
                                                    cursor: slots.length <= 1 ? "not-allowed" : "pointer",
                                                    fontSize: 18, fontWeight: 700,
                                                    flexShrink: 0,
                                                }}
                                            >×</button>
                                        </div>
                                        {/* Riga 2: turno (segmented I/II/III) + data */}
                                        <div style={{ display: "flex", alignItems: "center", gap: 10, paddingLeft: 42 }}>
                                            <div style={{ display: "flex", gap: 4, background: "#0a0a0a", border: "1px solid #333", borderRadius: 8, padding: 3 }}>
                                                {["I","II","III"].map(t => {
                                                    const active = sturno === t;
                                                    return (
                                                        <button
                                                            key={t}
                                                            type="button"
                                                            data-testid={`slot-turno-${i}-${t}`}
                                                            onClick={() => setSlotTurno(i, t)}
                                                            style={{
                                                                minWidth: 46,
                                                                background: active ? "#f59e0b" : "transparent",
                                                                color: active ? "black" : "#a3a3a3",
                                                                border: 0,
                                                                borderRadius: 6,
                                                                padding: "7px 10px",
                                                                fontWeight: 900,
                                                                fontFamily: "monospace",
                                                                fontSize: 13,
                                                                letterSpacing: 0.5,
                                                                cursor: "pointer",
                                                            }}
                                                        >{t}</button>
                                                    );
                                                })}
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
                                                <span style={{ fontSize: 11, opacity: 0.6, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase" }}>Data</span>
                                                <input
                                                    type="date"
                                                    data-testid={`slot-data-${i}`}
                                                    value={sdata}
                                                    onChange={e => setSlotData(i, e.target.value)}
                                                    style={{
                                                        background: "#0a0a0a",
                                                        color: "white",
                                                        border: "1px solid #333",
                                                        borderRadius: 6,
                                                        padding: "7px 10px",
                                                        fontSize: 13,
                                                        fontFamily: "monospace",
                                                        outline: "none",
                                                        colorScheme: "dark",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div style={{ marginTop: 14 }}>
                            <button
                                data-testid="btn-add-operatore"
                                onClick={addSlot}
                                disabled={slots.length >= MAX_OP}
                                style={{
                                    background: "transparent",
                                    border: "1px dashed #444",
                                    color: slots.length >= MAX_OP ? "#555" : "#f59e0b",
                                    borderRadius: 8,
                                    padding: "9px 14px",
                                    fontWeight: 700,
                                    cursor: slots.length >= MAX_OP ? "not-allowed" : "pointer",
                                    fontSize: 13,
                                }}
                            >+ Aggiungi operatore</button>
                        </div>

                        {operatori.length === 0 && (
                            <div style={{ marginTop: 12, fontSize: 12, opacity: 0.6, fontStyle: "italic" }}>
                                Nessun operatore saldatura registrato. Puoi comunque stampare una copia senza nome.
                            </div>
                        )}
                    </div>

                    <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button
                            data-testid="btn-conferma-stampa"
                            onClick={confirmStampa}
                            disabled={!canConfirm}
                            style={{
                                background: "#f59e0b", color: "black",
                                border: 0, borderRadius: 8,
                                padding: "12px 22px", fontWeight: 900, fontSize: 15,
                                cursor: "pointer",
                                letterSpacing: 0.3,
                            }}
                        >
                            Stampa {selectedIds.length > 0 ? `(${selectedIds.length} ${selectedIds.length === 1 ? "copia" : "copie"})` : "senza operatore"}
                        </button>
                        {selectedIds.length > 0 && (
                            <button
                                data-testid="btn-stampa-senza-op"
                                onClick={confirmStampaSenzaOp}
                                style={{
                                    background: "transparent", color: "white",
                                    border: "1px solid #333", borderRadius: 8,
                                    padding: "12px 18px", fontWeight: 700, fontSize: 14,
                                    cursor: "pointer",
                                }}
                            >Stampa senza operatore</button>
                        )}
                        <button
                            data-testid="btn-annulla-stampa"
                            onClick={() => window.history.back()}
                            style={{
                                background: "transparent", color: "#a3a3a3",
                                border: "1px solid #262626", borderRadius: 8,
                                padding: "12px 18px", fontWeight: 700, fontSize: 14,
                                cursor: "pointer",
                                marginLeft: "auto",
                            }}
                        >Annulla</button>
                    </div>
                </div>
            </div>
        );
    }

    // Render N pagine (una per operatore)
    return (
        <div className="a4-print-wrap">
            <style>{`
                @page { size: A4 portrait; margin: 14mm 15mm 12mm 15mm; }
                html, body { background: white !important; margin: 0 !important; padding: 0 !important; height: auto !important; min-height: 0 !important; overflow: visible !important; }
                body { color: black; font-family: system-ui, -apple-system, sans-serif; }
                #root, .grain { background: white !important; min-height: 0 !important; height: auto !important; padding: 0 !important; margin: 0 !important; }
                #root::before, #root::after, .grain::before, .grain::after { display: none !important; content: none !important; background: white !important; }
                .a4-print { padding: 0; background: white; color: black; font-size: 9.8pt; line-height: 1.22; width: 172mm; max-width: 172mm; margin: 0 auto; }
                .a4-print + .a4-print { page-break-before: always; break-before: page; margin-top: 12mm; }
                .a4-print * { color: black !important; background: white !important; border-color: black !important; box-shadow: none !important; text-shadow: none !important; }
                .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid black; padding-bottom: 2.5mm; margin-bottom: 2.5mm; }
                .head .co { font-weight: 900; font-size: 16pt; letter-spacing: -0.5px; }
                .head .co-sub { font-family: monospace; font-size: 8pt; letter-spacing: 2px; margin-top: 0.6mm; }
                .head .op-num { font-size: 22pt; font-weight: 900; font-family: monospace; letter-spacing: 1px; }
                .head .op-date { font-size: 8.5pt; margin-top: 0.4mm; }
                .qr-wrap { display: flex; align-items: center; gap: 3mm; margin-top: 1.8mm; }
                .qr-wrap .hint { font-size: 8pt; max-width: 55mm; line-height: 1.2; }
                .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0 6mm; margin: 1mm 0; }
                .row { display: flex; padding: 1.1mm 0; border-bottom: 1px dotted #888; font-size: 9.5pt; align-items: baseline; }
                .row .k { min-width: 34mm; font-weight: 700; text-transform: uppercase; font-size: 7.5pt; letter-spacing: 0.4px; }
                .row .v { flex: 1; }
                .section-title { margin-top: 2.2mm; margin-bottom: 0.6mm; font-family: monospace; font-size: 8.5pt; letter-spacing: 2px; text-transform: uppercase; border-bottom: 1.5px solid black; padding-bottom: 0.8mm; font-weight: 700; }
                .qta-box { margin-top: 3mm; border: 2.5px solid black; padding: 3mm 4mm; display: flex; align-items: center; gap: 5mm; page-break-inside: avoid; }
                .qta-box .qta-label { font-weight: 900; font-size: 12pt; text-transform: uppercase; letter-spacing: 0.5px; }
                .qta-box .qta-hint { font-size: 8pt; margin-top: 0.4mm; }
                .qta-box .qta-input { flex: 1; border-bottom: 2.5px solid black; height: 16mm; text-align: center; font-size: 22pt; font-family: monospace; }
                .firme { margin-top: 2.5mm; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 3mm; page-break-inside: avoid; }
                .firma-box { border: 1px solid black; padding: 2mm; height: 17mm; display: flex; flex-direction: column; justify-content: space-between; }
                .firma-box .fk { font-size: 7.5pt; text-transform: uppercase; letter-spacing: 0.4px; font-weight: 600; }
                .firma-box .fline { border-bottom: 1px solid black; margin-top: auto; }
                .note-box { margin-top: 1.5mm; padding: 2mm; border: 1px solid black; font-size: 9pt; page-break-inside: avoid; }
                .note-box .lab { font-size: 7.5pt; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 0.6mm; font-weight: 700; }
                @media print {
                    .no-print { display: none !important; }
                    html, body { height: auto !important; overflow: visible !important; }
                    .grain { min-height: 0 !important; height: auto !important; padding: 0 !important; }
                    .a4-print {
                        max-height: 270mm;
                        overflow: hidden;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .a4-print > * { page-break-inside: avoid; break-inside: avoid; }
                    .a4-print + .a4-print { margin-top: 0 !important; }
                }
                .no-print-toolbar { position: fixed; top: 8px; right: 8px; z-index: 100; display: flex; gap: 8px; }
                .no-print-toolbar button { padding: 8px 14px; border: 0; border-radius: 6px; font-weight: 700; cursor: pointer; }
                .no-print-toolbar .btn-print { background: #f59e0b !important; color: black !important; }
                .no-print-toolbar .btn-back { background: #262626 !important; color: white !important; }
            `}</style>

            <div className="no-print-toolbar no-print">
                <button className="btn-back" onClick={() => setPages(null)}>← Modifica</button>
                <button className="btn-print" onClick={() => window.print()}>Stampa</button>
            </div>

            {pages.map((op, idx) => (
                <div className="a4-print" key={idx} data-testid={`print-page-${idx}`}>
                    <div className="head">
                        <div>
                            <div className="co">{company}</div>
                            <div className="co-sub">LINEA SALDATURA</div>
                            <div className="qr-wrap">
                                <QRCodeSVG value={url} size={72} level="M" includeMargin={false} bgColor="#ffffff" fgColor="#000000" />
                                <div className="hint">Scansiona per aprire l'ordine sul gestionale</div>
                            </div>
                        </div>
                        <div style={{textAlign:"right"}}>
                            <div className="op-num">{o.numero}</div>
                            <div className="op-date">Creato: {(o.created_at || "").slice(0, 16).replace("T", " ")}</div>
                            <div className="op-date">Stato: <b>{(o.stato || "").toUpperCase()}</b>{isVas ? " · VASCHETTE" : ""}</div>
                            {pages.length > 1 && (
                                <div className="op-date" style={{marginTop:"1mm", fontFamily:"monospace"}}>Copia {idx + 1} di {pages.length}</div>
                            )}
                        </div>
                    </div>

                    <div className="section-title">Articolo</div>
                    <div className="grid2">
                        <div className="row"><span className="k">Codice</span><span className="v" style={{fontFamily:"monospace", fontWeight:700}}>{o.codice}</span></div>
                        <div className="row"><span className="k">Cliente</span><span className="v">{o.cliente_eff || o.cliente_nome || "-"}</span></div>
                        <div className="row" style={{gridColumn:"1 / span 2"}}><span className="k">Descrizione</span><span className="v">{o.descrizione}</span></div>
                        <div className="row"><span className="k">Larg. principale</span><span className="v" style={{fontFamily:"monospace"}}>{wPrinc} cm</span></div>
                        <div className="row"><span className="k">Soffietto</span><span className="v" style={{fontFamily:"monospace"}}>{wSoff} cm</span></div>
                        <div className="row"><span className="k">Lunghezza busta</span><span className="v" style={{fontFamily:"monospace"}}>{lLen} cm</span></div>
                        <div className="row"><span className="k">Spessore</span><span className="v" style={{fontFamily:"monospace"}}>{sMy} µm</span></div>
                        <div className="row"><span className="k">Colore</span><span className="v">{o.colore_nome || "-"}</span></div>
                        <div className="row"><span className="k">Fascetta</span><span className="v">{o.fascetta_codice ? `${o.fascetta_codice}${o.fascetta_desc ? " · " + o.fascetta_desc : ""}` : "-"}</span></div>
                    </div>

                    <div className="section-title">Pesi</div>
                    <div className="grid2">
                        <div className="row"><span className="k">Peso busta</span><span className="v" style={{fontFamily:"monospace"}}>{pBusta ? `${pBusta.toFixed(2)} g` : "-"}</span></div>
                        <div className="row"><span className="k">Peso rotolo</span><span className="v" style={{fontFamily:"monospace"}}>{pRotolo ? `${Math.round(pRotolo)} g` : "-"}</span></div>
                        <div className="row" style={{gridColumn:"1 / span 2"}}><span className="k">Peso {isVas ? "vaschetta" : "cartone"}</span><span className="v" style={{fontFamily:"monospace", fontWeight:700}}>{pCartone ? `${pCartone.toFixed(1)} kg` : "-"}</span></div>
                    </div>

                    <div className="section-title">Confezionamento</div>
                    <div className="grid2">
                        <div className="row" style={{gridColumn:"1 / span 2"}}><span className="k">Confezione</span><span className="v" style={{fontFamily:"monospace"}}>{confezRxB}</span></div>
                        <div className="row"><span className="k">Pedana</span><span className="v">{o.tipo_pedana_codice || "-"}</span></div>
                        {!isVas && <div className="row"><span className="k">Cartone</span><span className="v">{o.cartone_codice || "-"}</span></div>}
                        <div className="row"><span className="k">{unitTitle}/pedana</span><span className="v" style={{fontFamily:"monospace"}}>{o.cartoni_per_pedana_eff || "-"}</span></div>
                        <div className="row"><span className="k">{unitTitle} pianif.</span><span className="v" style={{fontSize:"12pt", fontFamily:"monospace", fontWeight:900}}>{o.cartoni_pianificati}</span></div>
                        <div className="row" style={{gridColumn:"1 / span 2"}}><span className="k">Imballo</span><span className="v" style={{fontWeight:700}}>{unitTitle}</span></div>
                    </div>

                    {o.note_articolo && <div className="note-box"><div className="lab">Note articolo</div>{o.note_articolo}</div>}

                    <div className="section-title">Lavorazione</div>
                    <div className="grid2">
                        <div className="row"><span className="k">Macchina</span><span className="v" style={{fontWeight:700}}>{o.impianto_nome || "-"}</span></div>
                        <div className="row"><span className="k">Piste</span><span className="v" style={{fontFamily:"monospace"}}>{o.piste_eff || "-"}</span></div>
                        <div className="row"><span className="k">Turno</span><span className="v" style={{fontFamily:"monospace", fontWeight:700}}>{op.turno || o.turno || "-"}</span></div>
                        <div className="row"><span className="k">Data</span><span className="v" style={{fontFamily:"monospace"}}>{fmtDataLunga(op.data) || data_ || "-"}</span></div>
                        <div className="row"><span className="k">Operatore</span><span className="v" style={{fontWeight: op.name ? 700 : 400}}>{op.name || (o.operatore_nome || "-")}</span></div>
                    </div>

                    {o.note && <div className="note-box"><div className="lab">Note ufficio</div>{o.note}</div>}

                    <div className="qta-box">
                        <div>
                            <div className="qta-label">Quantità prodotta</div>
                            <div className="qta-hint">(scrivi qui il totale)</div>
                        </div>
                        <div className="qta-input">{unitUpper}</div>
                    </div>

                    <div className="section-title">Chiusura ordine</div>
                    <div className="firme">
                        <div className="firma-box">
                            <div className="fk">Data / ora</div>
                            <div className="fline"></div>
                        </div>
                        <div className="firma-box">
                            <div className="fk">Firma operatore</div>
                            <div className="fline"></div>
                        </div>
                        <div className="firma-box">
                            <div className="fk">Firma ufficio</div>
                            <div className="fline"></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
